export const PHOTO_PLAN_DEFAULTS = {
  photoCount: 40,
  primaryFocus: 'mixed',
  tonePreferences: ['warm', 'grounded', 'candid'],
  includeBrotherhood: true,
  includeDiversity: true,
  notes: '',
};

export const PRIMARY_FOCUS_OPTIONS = [
  { value: 'mixed', label: 'Mixed focus' },
  { value: 'parenting', label: 'Parenting' },
  { value: 'marriage', label: 'Marriage' },
  { value: 'career', label: 'Career' },
  { value: 'brotherhood', label: 'Brotherhood' },
];

export const SEARCH_PLAN_CATEGORIES = [
  { key: 'parenting', label: 'Parenting' },
  { key: 'marriage', label: 'Marriage' },
  { key: 'career', label: 'Career' },
  { key: 'brotherhood', label: 'Brotherhood' },
];

export const TONE_OPTIONS = [
  'warm',
  'grounded',
  'masculine',
  'modern',
  'natural',
  'candid',
  'relational',
  'family-centered',
  'emotionally authentic',
];

const SEARCH_LINK_BUILDERS = {
  Pexels: (query) => `https://www.pexels.com/search/${encodeURIComponent(query)}/`,
  Unsplash: (query) => `https://unsplash.com/s/photos/${encodeURIComponent(query)}`,
};

export const getDayOfYear = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  return Math.floor(diff / 86400000);
};

export const copyTextToClipboard = async (text, successMessage, failureMessage, toast) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
    return true;
  } catch (err) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success(successMessage);
      return true;
    } catch (copyError) {
      toast.error(failureMessage);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

export const sanitizePhotoPlanPayload = (form) => ({
  photoCount: Math.min(100, Math.max(1, Number(form.photoCount) || PHOTO_PLAN_DEFAULTS.photoCount)),
  primaryFocus: form.primaryFocus || PHOTO_PLAN_DEFAULTS.primaryFocus,
  tonePreferences: Array.isArray(form.tonePreferences) ? form.tonePreferences : PHOTO_PLAN_DEFAULTS.tonePreferences,
  includeBrotherhood: !!form.includeBrotherhood,
  includeDiversity: !!form.includeDiversity,
  notes: form.notes?.trim() || '',
});

export const buildSearchPlanText = (plan) => {
  if (!plan) return '';

  const lines = ['Find Photos', ''];

  lines.push('Best sources');
  (plan.sourcePriority || []).forEach((source) => {
    lines.push(`- ${source.name}: ${source.reason}`);
  });
  lines.push('');

  lines.push('Suggested photo mix');
  SEARCH_PLAN_CATEGORIES.forEach(({ key, label }) => {
    lines.push(`- ${label}: ${plan.recommendedPhotoMix?.[key] ?? 0}`);
  });
  lines.push('');

  lines.push('Search phrases');
  SEARCH_PLAN_CATEGORIES.forEach(({ key, label }) => {
    lines.push(`${label}:`);
    (plan.searchPhrasesByCategory?.[key] || []).forEach((phrase) => {
      lines.push(`- ${phrase}`);
    });
  });
  lines.push('');

  lines.push('What to look for');
  (plan.visualTraitsToPrefer || []).forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push('What to reject');
  (plan.visualTraitsToReject || []).forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  lines.push('Fast workflow');
  (plan.fastWorkflow || []).forEach((item) => {
    lines.push(`- ${item}`);
  });
  lines.push('');

  if ((plan.upgradedSearches || []).length > 0) {
    lines.push('Upgraded searches');
    (plan.upgradedSearches || []).forEach((item) => {
      lines.push(`- ${item}`);
    });
  }

  return lines.join('\n').trim();
};

export const buildQuickSearchLinks = (plan) => {
  if (!plan) return [];

  return SEARCH_PLAN_CATEGORIES.map(({ key, label }) => ({
    key,
    label,
    pexelsLinks: (plan.searchPhrasesByCategory?.[key] || []).slice(0, 2).map((phrase) => ({
      phrase,
      url: SEARCH_LINK_BUILDERS.Pexels(phrase),
    })),
    unsplashLinks: (plan.searchPhrasesByCategory?.[key] || []).slice(2, 3).map((phrase) => ({
      phrase,
      url: SEARCH_LINK_BUILDERS.Unsplash(phrase),
    })),
  }));
};

export const getPhotoPlanErrorMessage = (detail) => {
  if (!detail) return 'Failed to generate search plan';

  if (detail.includes('OPENAI_API_KEY')) {
    return 'Photo search planning is not set up yet. Add OPENAI_API_KEY to the backend environment, then restart the backend and try again.';
  }

  if (detail.includes('OpenAI support is not available')) {
    return 'Photo search planning is not available on this deployment yet. Install the backend OpenAI dependency and redeploy.';
  }

  return detail;
};

export const getPhotoPlanRequestFailureMessage = (error) => {
  const detail = error?.message || '';

  if (detail.includes('request timeout')) {
    return 'Photo search planning timed out. Confirm the backend is running, then try again.';
  }

  if (detail.includes('Failed to fetch')) {
    return 'Could not reach the backend. Restart the backend server so it reloads backend/.env, then try again.';
  }

  return 'Failed to generate search plan';
};

export const getPhotoCandidatesErrorMessage = (detail) => {
  if (!detail) return 'Failed to find sidebar photos';

  if (detail.includes('PEXELS_API_KEY')) {
    return 'Sidebar photo discovery is not set up yet. Add PEXELS_API_KEY to the backend environment, restart the backend, and try again.';
  }

  if (detail.includes('Pexels search failed')) {
    return 'Pexels search failed. Check the backend API key or provider quota, then try again.';
  }

  return detail;
};

export const getPhotoCandidatesRequestFailureMessage = (error) => {
  const detail = error?.message || '';

  if (detail.includes('request timeout')) {
    return 'Finding sidebar photos timed out. The request may need another try, especially on the first run.';
  }

  if (detail.includes('Failed to fetch')) {
    return 'Could not reach the backend. Confirm the frontend and backend are both running, then try again.';
  }

  return 'Failed to find sidebar photos';
};

export const getPhotoImportErrorMessage = (detail) => {
  if (!detail) return 'Failed to add kept photos to the sidebar';

  if (detail.includes('PEXELS_API_KEY')) {
    return 'Sidebar photo discovery is not set up yet. Add PEXELS_API_KEY to the backend environment, restart the backend, and try again.';
  }

  if (detail.includes('Only Pexels sidebar candidates can be imported')) {
    return 'Only Pexels photo candidates can be added to the sidebar right now.';
  }

  return detail;
};

export const getPhotoImportRequestFailureMessage = (error) => {
  const detail = error?.message || '';

  if (detail.includes('request timeout')) {
    return 'Adding kept photos took too long. Try again in a moment.';
  }

  if (detail.includes('Failed to fetch')) {
    return 'Could not reach the backend while adding kept photos. Confirm the backend is running, then try again.';
  }

  return 'Failed to add kept photos to the sidebar';
};
